/**
 * Validation Types:
 * Use 'Validation' for checking prop type in compoents.
 *
 * Validtation object can come back from API in four shapes.
 * Use the four specific types below when we need to perform specific typechecking
 * (like before sending a specific type of validation data to API).
 */

export enum ValidationsEnum {
  MinutesOff = 'free_minutes',
  DollarsOff = 'dollar_discount',
  PercentOff = 'percent_discount',
  WeekdayWeekend = 'free_minutes_based_on_weekday',
  FlatRate = 'flat_rate',
  PassFlatRate = 'pass_flat_rate',
  PassPercentDiscount = 'pass_percent_discount',
  Tabular = 'tabular_discount',
}

export type ValidationType = ValidationsEnum;

export enum ValidationProductTypeEnum {
  ParkingPass = 'pass',
  ParkingVisit = 'visit',
}

export enum TabularDiscountType {
  Dollar = 'DOLLAR',
  Percent = 'PERCENT',
  Minutes = 'MINUTES',
  FlatRate = 'FLAT_RATE',
}

export type ValidationProductType = ValidationProductTypeEnum;

export interface ValidationBase {
  active: boolean;
  addedAt: number;
  allowMaxExceeded?: boolean;
  gracePeriodMinutes?: number | null;
  id: number;
  planType: ValidationType;
  productType: ValidationProductType;
  updatedAt: number;
  validationPlanDefId?: number;
  validationPlanId: number;
  validationTerms: string;
  vendorName: string;
}

/**
 * TabularValidationStep as returned by the API (response/view).
 * Note: cumulativeDuration is in milliseconds.
 */
export interface TabularValidationStep {
  cumulativeDuration: number;
  discountAmount: number;
}

/**
 * TabularValidationStep for form submissions (request).
 * Note: cumulativeDurationMinutes is in minutes.
 */
export interface TabularValidationStepForm {
  cumulativeDurationMinutes: number;
  discountAmount: number;
}

/** @deprecated Use TabularValidationStep instead */
export type PlanStep = TabularValidationStep;

export interface Validation extends ValidationBase {
  dollarAmount?: number;
  effectiveFrom?: string;
  expiresAt?: string;
  freeMinutes?: number;
  gracePeriodMinutes?: number | null; // null and 0 means NO grace period
  maxDurationMinutes?: number | null; // null means free parking for the entire visit (no max limit)
  percent?: number;
  price?: number;
  tabularDiscountType?: TabularDiscountType;
  tabularSteps?: TabularValidationStep[];
  weekdayMaxDuration?: number;
  weekendMaxDuration?: number;
}

export interface FreeMinutesValidation extends ValidationBase {
  freeMinutes: number;
  planType: ValidationsEnum.MinutesOff;
  productType: ValidationProductTypeEnum.ParkingVisit;
}

export interface PercentDiscountValidation extends ValidationBase {
  maxDurationMinutes: number | null;
  percent: number;
  planType: ValidationsEnum.PercentOff;
  productType: ValidationProductTypeEnum.ParkingVisit;
}

export interface DollarDiscountValidation extends ValidationBase {
  dollarAmount: number;
  maxDurationMinutes: number | null;
  planType: ValidationsEnum.DollarsOff;
  productType: ValidationProductTypeEnum.ParkingVisit;
}

export interface FreeMinutesBasedOnWeekdayValidation extends ValidationBase {
  planType: ValidationsEnum.WeekdayWeekend;
  productType: ValidationProductTypeEnum.ParkingVisit;
  weekdayMaxDurationMinutes: number;
  weekendMaxDurationMinutes: number;
}

export interface FreeMinutesBasedOnWeekdayEntValidation extends ValidationBase {
  planType: ValidationsEnum.WeekdayWeekend;
  productType: ValidationProductTypeEnum.ParkingVisit;
  weekdayMaxDuration: number;
  weekendMaxDuration: number;
}

export interface FlatRateValidation extends ValidationBase {
  maxDurationMinutes: number;
  planType: ValidationsEnum.FlatRate;
  price: number;
  productType: ValidationProductTypeEnum.ParkingVisit;
}

export interface PassFlatRateValidation extends ValidationBase {
  planType: ValidationsEnum.PassFlatRate;
  price: number;
  productType: ValidationProductTypeEnum.ParkingPass;
}

export interface PassPercentDiscountValidation extends ValidationBase {
  percent: number;
  planType: ValidationsEnum.PassPercentDiscount;
  productType: ValidationProductTypeEnum.ParkingPass;
}

export interface TabularValidation extends ValidationBase {
  planType: ValidationsEnum.Tabular;
  productType: ValidationProductTypeEnum.ParkingVisit;
  tabularDiscountType: TabularDiscountType;
  tabularSteps: TabularValidationStep[];
}

export type ValidationPlan =
  | FreeMinutesValidation
  | PercentDiscountValidation
  | DollarDiscountValidation
  | FreeMinutesBasedOnWeekdayValidation
  | FreeMinutesBasedOnWeekdayEntValidation
  | FlatRateValidation
  | PassFlatRateValidation
  | PassPercentDiscountValidation
  | TabularValidation;

export type CreateFreeMinutesValidation = Omit<FreeMinutesValidation, keyof ValidationBase>;

export type CreatePercentDiscountValidation = Omit<PercentDiscountValidation, keyof ValidationBase>;

export type CreateDollarDiscountValidation = Omit<DollarDiscountValidation, keyof ValidationBase>;

export type CreateFreeMinutesBasedOnWeekdayValidation = Omit<
  FreeMinutesBasedOnWeekdayValidation,
  keyof ValidationBase
>;

export type CreateFlatRateValidation = Omit<FlatRateValidation, keyof ValidationBase>;

export type CreateTabularValidation = Omit<TabularValidation, keyof ValidationBase>;

/**
 * Form payload for creating a new tabular validation via API.
 * Use this when submitting to POST /api/admin/enterprise/:id/location/:id/create-sign
 */
export interface NewTabularDiscountForm {
  discountType: TabularDiscountType;
  tabularSteps: TabularValidationStepForm[];
}

export type ValidationMap = {
  [key: number]: Validation;
};

export type CreateValidationResponse = Validation;

export type ValidationStatus = 'Active' | 'Not Started' | 'Expired' | 'Fully Redeemed';

/**
 * ValidationPlanView as returned by the backend API.
 * This is different from ValidationPlan which is a discriminated union.
 * The backend returns all fields as optional in a single object.
 */
export interface ValidationPlanView {
  addedAt: number;
  allowMaxExceeded?: boolean | null;
  dollarAmount: number | null;
  freeMinutes: number | null;
  gracePeriodMinutes: number | null;
  id: number;
  maxDurationMinutes: number | null;
  percent: number | null;
  planType: ValidationType;
  price: number | null;
  productType: ValidationProductType;
  tabularDiscountType?: TabularDiscountType | null;
  tabularSteps?: TabularValidationStep[] | null;
  type: ValidationType; // deprecated but still returned
  validationPlanId: number;
  weekdayMaxDuration: number | null;
  weekendMaxDuration: number | null;
}

export type GetValidationsResponseValidation = {
  active: boolean;
  addedAt: number;
  cooldownMins: number | null;
  effectiveFrom: string | null;
  expiresAt: string | null;
  id: number;
  maxRedemptions: number | null;
  name: string | null;
  partiallyDiscountable: boolean;
  updatedAt: number;
  validationPlan: ValidationPlanView;
  validationStatus: ValidationStatus | null;
  vendorName: string;
};

export type GetValidationsResponse = GetValidationsResponseValidation[];

export const isPassValidation = (validation?: ValidationsEnum): boolean =>
  validation
    ? validation === ValidationsEnum.PassFlatRate ||
      validation === ValidationsEnum.PassPercentDiscount
    : false;

export const isTabularValidation = (
  validation?: ValidationsEnum,
  enableTabularValidations: boolean = false,
): boolean =>
  enableTabularValidations && (validation ? validation === ValidationsEnum.Tabular : false);
